/**
 * API client — wraps fetch() with auth header injection and error handling.
 *
 * Only the endpoints needed by the React app's ported routes are included.
 * Extend as more routes are migrated.
 */

import { getAccessToken } from "./auth";
import type {
  Announcement,
  RecordingSummary,
  RecordingTranscript,
  SubscriptionStatus,
} from "./sharedApiTypes";

export type { Announcement, RecordingSummary, RecordingTranscript, SubscriptionStatus };
import { API_URL } from "./env";
export { API_URL };
import { getImpersonation } from "./impersonation";
import type { ClinicalAnalysisResult } from "@/types/api";

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, "");

/**
 * Build request headers with the Bearer token (when present), any extra
 * headers, and the active impersonation user — the auth-header shape every
 * authed FormData/raw call shares.
 */
function buildAuthHeaders(
  token: string | null,
  extra: Record<string, string> = {},
): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  const impersonation = getImpersonation();
  if (impersonation) headers["X-Impersonate-User"] = impersonation.user_id;
  return headers;
}

/**
 * Dev-proxy gate: route API calls through a relative path (handled by the
 * Vite dev proxy) ONLY in dev builds, and only on a known-local host. In a
 * production build this is always false, so relative-path routing can never
 * leak into prod hosting where no such proxy exists.
 */
function shouldUseDevProxy(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".trycloudflare.com") ||
    /^192\.168\.\d+\.\d+$/.test(hostname)
  );
}

/**
 * Build a request URL for `path`. The single source of truth for URL building
 * across every web fetch wrapper.
 *
 * - Absolute URLs (`http...`) are returned untouched.
 * - In a dev build on a local host, returns the relative `path` so the Vite
 *   dev proxy can forward it (avoids CORS / mixed-origin in dev).
 * - Otherwise prefixes the configured `base` (defaults to {@link API_URL}).
 *
 * `base` lets callers (e.g. the widget passkey handoff) target an explicit
 * non-default API origin; such bases are always used verbatim — the dev proxy
 * only ever applies to the default {@link API_URL}.
 */
export function apiUrl(path: string, base: string = API_URL): string {
  if (path.startsWith("http")) return path;
  if (base === API_URL && shouldUseDevProxy()) return path;
  if (!base) return path;
  return `${trimTrailingSlash(base)}${path}`;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Extract a human-readable error detail from a non-2xx response body,
 * falling back to "<status> <statusText>". Shared by authFetch /
 * authFetchJson so both report the same backend detail message.
 */
export async function parseErrorDetail(response: Response): Promise<string> {
  let detail = `${response.status} ${response.statusText}`;
  try {
    const body = (await response.json()) as { detail?: string; message?: string };
    detail = body.detail ?? body.message ?? detail;
  } catch {
    /* ignore */
  }
  return detail;
}

/**
 * Low-level authed fetch: injects the bearer token, content-type and
 * impersonation headers, then returns the raw Response WITHOUT checking
 * status. Use only when the caller needs to inspect `response.ok`/status
 * itself (e.g. to branch on specific codes). For the common case prefer
 * `authFetch`, which throws on non-2xx.
 */
export async function authFetchRaw(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError(401, "Niet ingelogd. Log opnieuw in.");
  }

  const url = apiUrl(path);
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  // Never force JSON on a FormData body: the browser must set its own
  // multipart Content-Type (with boundary) for the server to parse it.
  if (init.body && !headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const impersonation = getImpersonation();
  if (impersonation) {
    headers.set("X-Impersonate-User", impersonation.user_id);
  }

  return await fetch(url, { ...init, headers });
}

/**
 * Authed fetch that throws an {@link ApiError} on any non-2xx response,
 * so callers can treat resolution as success. Use `authFetchRaw` when you
 * need to inspect the status yourself.
 */
export async function authFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const response = await authFetchRaw(path, init);
  if (!response.ok) {
    throw new ApiError(response.status, await parseErrorDetail(response));
  }
  return response;
}

export async function authFetchJson<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await authFetchRaw(path, init);
  if (!response.ok) {
    throw new ApiError(response.status, await parseErrorDetail(response));
  }
  return (await response.json()) as T;
}

// ── Richtlijnen (clinical analysis) ─────────────────────────────────────

export async function runClinicalAnalysis(
  clinicalText: string,
): Promise<ClinicalAnalysisResult> {
  return await authFetchJson<ClinicalAnalysisResult>("/api/clinical-analysis", {
    method: "POST",
    body: JSON.stringify({
      clinical_text: clinicalText,
      max_results: 40,
      include_class_iii: false,
    }),
  });
}

// ── Transcription ──────────────────────────────────────────────────────

import type { TranscribeResult } from "@/types/api";

export interface TranscribeOptions {
  mode: string;
  quick?: boolean;
  pipelineVersion?: string;
  previousReport?: string;
  reportHeader?: string;
  situation?: string;
}

/**
 * Upload an audio blob to /api/transcribe and return the result.
 * Supports quick mode (appended as "mode|quick").
 */
export async function transcribeAudio(
  blob: Blob,
  options: TranscribeOptions,
): Promise<TranscribeResult> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "Niet ingelogd. Log opnieuw in.");

  const form = new FormData();
  const ext = blob.type.split("/")[1]?.split(";")[0] ?? "webm";
  let mode = options.quick ? `${options.mode}|quick` : options.mode;
  if (options.pipelineVersion?.trim()) {
    mode = `${mode}|pv=${options.pipelineVersion.trim()}`;
  }
  form.append("audio", blob, `recording.${ext}`);
  form.append("mode", mode);
  if (options.previousReport) form.append("previous_report", options.previousReport);
  if (options.reportHeader) form.append("report_header", options.reportHeader);
  if (options.situation) form.append("situation", options.situation);

  const transcribeHeaders = buildAuthHeaders(token);
  const resp = await fetch(apiUrl("/api/transcribe"), {
    method: "POST",
    headers: transcribeHeaders,
    body: form,
  });
  if (!resp.ok) {
    let detail = `${resp.status} ${resp.statusText}`;
    try {
      const body = (await resp.json()) as { detail?: string };
      detail = body.detail ?? detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(resp.status, detail);
  }
  return (await resp.json()) as TranscribeResult;
}

export interface VerslagSection {
  id: string;
  title: string;
  text: string;
}

export interface AmbientTranscribeResult {
  text: string;
  structured_json: Record<string, unknown> | null;
  verslag_sections?: VerslagSection[];
  transcript: string;
  tier2: Record<string, unknown>;
}

/** Attach the Gesprek result (summary + buckets + verslag) to a recording. */
export async function linkRecordingAmbientResult(
  recordingId: string,
  input: {
    text: string;
    structuredJson?: Record<string, unknown> | null;
    verslagSections?: VerslagSection[];
  },
): Promise<void> {
  await authFetchJson<{ id: string }>(`/api/recordings/${recordingId}/ambient-result`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      text: input.text,
      structured_json: input.structuredJson ?? null,
      verslag_sections: input.verslagSections ?? null,
    }),
  });
}

/**
 * Upload an audio blob to /api/transcribe-ambient — the offline
 * counterpart to the streaming WS scribe. Runs STT + Tier 2/3 and
 * returns the narrative text + tri-state structured JSON.
 */
export async function transcribeAmbient(blob: Blob): Promise<AmbientTranscribeResult> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "Niet ingelogd. Log opnieuw in.");

  const form = new FormData();
  const ext = blob.type.split("/")[1]?.split(";")[0] ?? "webm";
  form.append("audio", blob, `consult.${ext}`);

  const headers = buildAuthHeaders(token);

  const resp = await fetch(apiUrl("/api/transcribe-ambient"), {
    method: "POST",
    headers,
    body: form,
  });
  if (!resp.ok) {
    let detail = `${resp.status} ${resp.statusText}`;
    try {
      const body = (await resp.json()) as { detail?: string };
      detail = body.detail ?? detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(resp.status, detail);
  }
  return (await resp.json()) as AmbientTranscribeResult;
}

// ── Recordings (audio archive — Hetzner S3 backed) ─────────────────────

export interface RecordingsListResponse {
  recordings: RecordingSummary[];
  total: number;
}

export type RecordingListFilters = {
  q?: string;
  status?: string;
  from?: string;
  to?: string;
};

export type RecordingListParams = RecordingListFilters & {
  offset?: number;
  limit?: number;
};

export interface UploadRecordingResult {
  id: string;
  s3_key: string;
  size_bytes: number;
  expires_at: string;
  display_name: string;
  status: "uploading" | "uploaded" | "failed";
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadRecordingOptions {
  recordedAt?: Date;
  durationSeconds?: number;
  idempotencyKey: string;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

// XMLHttpRequest is used here (not fetch) because fetch lacks broadly-supported
// upload progress events. fetch + ReadableStream uploads are still gated behind
// HTTP/2 and not available in Safari/Firefox at usable levels.
function xhrUpload(
  url: string,
  form: FormData,
  headers: Record<string, string>,
  onProgress?: (progress: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<UploadRecordingResult> {
  return new Promise<UploadRecordingResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress({ loaded: e.loaded, total: e.total });
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadRecordingResult);
        } catch {
          reject(new ApiError(xhr.status, "Antwoord niet leesbaar."));
        }
        return;
      }
      let detail = `${xhr.status} ${xhr.statusText}`;
      try {
        const body = JSON.parse(xhr.responseText) as { detail?: string; message?: string };
        detail = body.detail ?? body.message ?? detail;
      } catch {
        /* ignore */
      }
      reject(new ApiError(xhr.status, detail));
    };
    xhr.onerror = () => reject(new ApiError(0, "Netwerkfout tijdens upload."));
    xhr.onabort = () => reject(new ApiError(0, "Upload geannuleerd."));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.send(form);
  });
}

export async function uploadRecording(
  blob: Blob,
  source: "live" | "ambient",
  options: UploadRecordingOptions,
): Promise<UploadRecordingResult> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "Niet ingelogd. Log opnieuw in.");

  const form = new FormData();
  const ext = blob.type.split("/")[1]?.split(";")[0] ?? "webm";
  form.append("audio", blob, `recording.${ext}`);
  form.append("source", source);
  if (options.recordedAt) form.append("recorded_at", options.recordedAt.toISOString());
  if (options.durationSeconds != null) {
    form.append("duration_seconds", String(options.durationSeconds));
  }

  const headers = buildAuthHeaders(token, { "Idempotency-Key": options.idempotencyKey });

  return xhrUpload(
    apiUrl("/api/recordings/upload"),
    form,
    headers,
    options.onProgress,
    options.signal,
  );
}

export interface RetryRecordingOptions {
  idempotencyKey: string;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

export async function retryRecordingUpload(
  recordingId: string,
  blob: Blob,
  mimeType: string,
  options: RetryRecordingOptions,
): Promise<UploadRecordingResult> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "Niet ingelogd. Log opnieuw in.");

  const form = new FormData();
  const ext = mimeType.split("/")[1]?.split(";")[0] ?? "webm";
  form.append("audio", new Blob([blob], { type: mimeType }), `recording.${ext}`);

  const headers = buildAuthHeaders(token, { "Idempotency-Key": options.idempotencyKey });

  return xhrUpload(
    apiUrl(`/api/recordings/${recordingId}/retry`),
    form,
    headers,
    options.onProgress,
    options.signal,
  );
}

export async function listRecordings(
  paramsInput: RecordingListParams = {},
): Promise<RecordingsListResponse> {
  const params = new URLSearchParams();
  params.set("offset", String(paramsInput.offset ?? 0));
  params.set("limit", String(paramsInput.limit ?? 25));
  if (paramsInput.q) params.set("q", paramsInput.q);
  if (paramsInput.status) params.set("status", paramsInput.status);
  if (paramsInput.from) params.set("from", paramsInput.from);
  if (paramsInput.to) params.set("to", paramsInput.to);
  return await authFetchJson<RecordingsListResponse>(`/api/recordings?${params}`);
}

export async function deleteRecording(id: string): Promise<void> {
  await authFetchJson<{ ok: boolean }>(`/api/recordings/${id}`, { method: "DELETE" });
}

export function recordingDownloadUrl(id: string): string {
  return apiUrl(`/api/recordings/${id}/download`);
}

export async function downloadRecording(id: string): Promise<Blob> {
  const resp = await authFetchRaw(`/api/recordings/${id}/download`);
  if (!resp.ok) throw new ApiError(resp.status, "Kon opname niet ophalen.");
  return await resp.blob();
}


export interface TranscriptInput {
  text: string;
  mode?: string;
  contentType?: string;
  todos?: string[];
}

export async function linkRecordingTranscript(
  recordingId: string,
  input: TranscriptInput,
): Promise<void> {
  await authFetchJson<{ id: string }>(`/api/recordings/${recordingId}/transcript`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      text: input.text,
      mode: input.mode ?? null,
      content_type: input.contentType ?? null,
      todos: input.todos ?? null,
    }),
  });
}

export async function getRecordingTranscript(
  recordingId: string,
): Promise<RecordingTranscript> {
  return await authFetchJson<RecordingTranscript>(
    `/api/recordings/${recordingId}/transcript`,
  );
}

export interface RecordingAmbientResult {
  text: string;
  structured_json: Record<string, unknown>;
  verslag_sections?: VerslagSection[];
  patient_ref?: string;
  session_id: string | null;
  updated_at: string | null;
}

/** The stored Gesprek result: summary, buckets and the section verslag. */
export async function getRecordingAmbientResult(
  recordingId: string,
): Promise<RecordingAmbientResult> {
  return await authFetchJson<RecordingAmbientResult>(
    `/api/recordings/${recordingId}/ambient-result`,
  );
}

export async function updateRecordingTranscript(
  recordingId: string,
  input: TranscriptInput,
): Promise<void> {
  await authFetchJson<{ id: string }>(`/api/recordings/${recordingId}/transcript`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      text: input.text,
      mode: input.mode ?? null,
      content_type: input.contentType ?? null,
      todos: input.todos ?? null,
    }),
  });
}

// ── Recording feedback (per-recording thumbs up/down + comment) ────────

export type RecordingFeedback = {
  id: string;
  recording_id: string | null;
  recording_label: string;
  rating: 1 | -1;
  comment: string | null;
  status: "new" | "treated";
  admin_reply: string | null;
  treated_at: string | null;
  seen_at: string | null;
  created_at: string;
  updated_at: string | null;
};

export async function upsertRecordingFeedback(
  recordingId: string,
  body: { rating: 1 | -1; comment?: string },
): Promise<RecordingFeedback> {
  return authFetchJson<RecordingFeedback>(`/api/recordings/${recordingId}/feedback`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function listMyRecordingFeedback(): Promise<{
  feedback: RecordingFeedback[];
  total: number;
  unread_count: number;
}> {
  return authFetchJson(`/api/recordings/feedback/mine?limit=200`);
}

export async function markRecordingFeedbackSeen(feedbackId: string): Promise<void> {
  await authFetchJson(`/api/recordings/feedback/${feedbackId}/seen`, { method: "POST" });
}

export type AdminRecordingFeedback = RecordingFeedback & {
  user_id: string;
  user_email: string;
  user_full_name: string;
  organization_id: string | null;
  organization_name: string;
  department_id: string | null;
  treated_by: string | null;
};

export async function adminListRecordingFeedback(opts: {
  status?: "new" | "treated";
  offset?: number;
  limit?: number;
  sort?: "newest" | "oldest";
} = {}): Promise<{ feedback: AdminRecordingFeedback[]; total: number }> {
  const params = new URLSearchParams({ limit: String(opts.limit ?? 50) });
  if (opts.status) params.set("status", opts.status);
  if (opts.offset) params.set("offset", String(opts.offset));
  if (opts.sort) params.set("sort", opts.sort);
  return authFetchJson(`/api/admin/recording-feedback?${params.toString()}`);
}

export async function adminPatchRecordingFeedback(
  id: string,
  body: { status: "new" | "treated"; admin_reply?: string },
): Promise<AdminRecordingFeedback> {
  return authFetchJson(`/api/admin/recording-feedback/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── Todos (CarmenTodo voice-dictated follow-ups) ────────────────────────

export type Todo = {
  id: string;
  created_at: string;
  recording_id: string | null;
  recording_label: string;
  text: string;
  /** Scanned/typed patient number of the linked recording (read-time lookup; "" when none). */
  patient_ref?: string;
  done_at: string | null;
  done_by: string | null;
  /** Who checked it off when that was not the creator (e.g. the secretariat); "" otherwise. */
  done_by_name?: string;
};

export async function listMyTodos(): Promise<{
  todos: Todo[];
  total: number;
  open_count: number;
}> {
  return authFetchJson(`/api/todos/mine?limit=200`);
}

export async function setTodoDone(id: string, done: boolean): Promise<Todo> {
  return authFetchJson(`/api/todos/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ done }),
  });
}

export type AdminTodo = Todo & {
  user_id: string;
  user_email: string;
  user_full_name: string;
  organization_id: string | null;
  department_id: string | null;
  association_id: string | null;
  association_name: string;
};

export async function adminListTodos(
  opts: {
    associationId?: string;
    status?: "open" | "done";
    offset?: number;
    limit?: number;
    sort?: "newest" | "oldest";
  } = {},
): Promise<{ todos: AdminTodo[]; total: number }> {
  const params = new URLSearchParams({ limit: String(opts.limit ?? 50) });
  if (opts.associationId) params.set("association_id", opts.associationId);
  if (opts.status) params.set("status", opts.status);
  if (opts.offset) params.set("offset", String(opts.offset));
  if (opts.sort) params.set("sort", opts.sort);
  return authFetchJson(`/api/admin/todos?${params.toString()}`);
}

/** Secretariat checks off / reopens a todo of their department. */
export async function adminSetTodoDone(id: string, done: boolean): Promise<AdminTodo> {
  return authFetchJson(`/api/admin/todos/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ done }),
  });
}

export async function adminListAssociations(): Promise<{ id: string; name: string }[]> {
  const resp = await authFetchJson<{ associations: { id: string; name: string }[] }>(
    `/api/admin/associations`,
  );
  return resp.associations ?? [];
}

// ── Announcements (org-scoped banners) ─────────────────────────────────


export async function listAnnouncements(): Promise<Announcement[]> {
  const data = await authFetchJson<{ announcements: Announcement[] }>("/api/announcements");
  return data.announcements;
}

// ── Admin: org retention ───────────────────────────────────────────────

export interface RetentionImpact {
  affected_count: number;
  affected_users: number;
  oldest: string | null;
  newest: string | null;
}

export async function getRetentionImpact(orgId: string, days: number): Promise<RetentionImpact> {
  return await authFetchJson<RetentionImpact>(
    `/api/admin/organizations/${orgId}/retention-impact?days=${days}`,
  );
}

export async function updateOrganizationRetention(orgId: string, days: number): Promise<void> {
  await authFetchJson<unknown>(`/api/admin/organizations/${orgId}`, {
    method: "PATCH",
    body: JSON.stringify({ recording_retention_days: days }),
  });
}

// ── Feedback (free text + correction pairs) ────────────────────────────

export interface FeedbackOptions {
  category?: string;
  mode?: string;
  metadata?: Record<string, unknown>;
}

export async function submitFeedback(
  message: string,
  options?: FeedbackOptions,
  audioBlob?: Blob,
): Promise<void> {
  const opts = options ?? {};
  const metadataObj = {
    page: window.location.pathname,
    mode: opts.mode ?? "",
    ...(opts.metadata ?? {}),
  };
  if (audioBlob) {
    const token = await getAccessToken();
    const form = new FormData();
    form.append("text", message);
    form.append("category", opts.category ?? "general");
    form.append("metadata_json", JSON.stringify(metadataObj));
    form.append("audio", audioBlob, "recording.webm");
    const headers = buildAuthHeaders(token);
    const resp = await fetch(`${API_URL}/api/feedback`, {
      method: "POST",
      headers,
      body: form,
    });
    if (!resp.ok) {
      let detail = "Kon feedback niet versturen.";
      try {
        const body = (await resp.json()) as { detail?: string };
        if (body.detail) detail = body.detail;
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    return;
  }
  await authFetchJson("/api/feedback", {
    method: "POST",
    body: JSON.stringify({
      text: message,
      category: opts.category ?? "general",
      metadata: metadataObj,
    }),
  });
}

// ── Study lookup (PubMed/PICO tooltip) ─────────────────────────────────

export interface StudyDetail {
  title: string;
  authors: string;
  journal: string;
  year: number | null;
  doi: string;
  pmid: string;
  abstract: string;
  study_acronym: string;
  pico: {
    population: string;
    intervention: string;
    comparator: string;
    outcome: string;
    design?: string;
    conclusion?: string;
  } | null;
  resolved: boolean;
}

const _studyCache = new Map<string, StudyDetail | null>();

export async function lookupStudy(
  query: string,
  guideline = "",
): Promise<StudyDetail | null> {
  const key = `${guideline}:${query}`.toLowerCase().trim();
  if (_studyCache.has(key)) return _studyCache.get(key) ?? null;
  try {
    let url = `/api/guidelines/studies?query=${encodeURIComponent(query)}`;
    if (guideline) url += `&guideline=${encodeURIComponent(guideline)}`;
    const resp = await authFetchRaw(url);
    if (!resp.ok) {
      _studyCache.set(key, null);
      return null;
    }
    const data = (await resp.json()) as { studies?: StudyDetail[] };
    const study = data.studies && data.studies.length > 0 ? data.studies[0] : null;
    _studyCache.set(key, study);
    return study;
  } catch {
    _studyCache.set(key, null);
    return null;
  }
}

// ── Subscription status ─────────────────────────────────────────


export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  return authFetchJson<SubscriptionStatus>("/api/subscription/status");
}

// ── Widget (desktop-app auto-update) ───────────────────────────

export interface WidgetLatest {
  version: string;
  notes: string;
  pub_date: string;
  mandatory: boolean;
  platforms: { "windows-x86_64": { signature: string; url: string } };
  macos: { url: string };
}

export async function getLatestWidget(): Promise<WidgetLatest> {
  const response = await fetch(apiUrl("/api/widget/latest"));
  if (!response.ok) throw new ApiError(response.status, await parseErrorDetail(response));
  return (await response.json()) as WidgetLatest;
}

export async function downloadWidget(platform: "windows" | "macos"): Promise<Blob> {
  const resp = await fetch(apiUrl(`/api/widget/dl?platform=${platform}`));
  if (!resp.ok) throw new ApiError(resp.status, "Kon download niet ophalen.");
  return await resp.blob();
}
